import { Box, Flex, Grid, Heading, Text } from '@chakra-ui/react'
import { DDSTheme, ddsTokens } from '@ds/index'

import { MeetingBookedData } from '../types'
import ChartSpinner from './ChartSpinner'

type Props = {
	meetingsBookedData: MeetingBookedData[] | undefined
	isLoading: boolean
}

type FunnelBoxProps = {
	title: string
	value: number
	description: string
	filledPercentage: number
	color: string
	position: 'first' | 'middle' | 'last'
}

const FunnelBox = ({
	title,
	value,
	description,
	filledPercentage,
	color,
	position,
}: FunnelBoxProps) => {
	const tokens = ddsTokens[DDSTheme.light]

	const borderStyles = {
		first: {
			borderRight: '1px solid',
			borderColor: tokens.primitive.light.gray[100],
		},
		middle: {
			borderRight: '1px solid',
			borderColor: tokens.primitive.light.gray[100],
		},
		last: {},
	}

	return (
		<Box p={4} {...borderStyles[position]}>
			<Text
				fontSize="sm"
				fontWeight="medium"
				mb={2}
				color={tokens.primitive.light.gray[800]}
			>
				{title}
			</Text>
			<Flex h="60px" borderRadius="md" mb={4} overflow="hidden">
				<Box
					w={`${filledPercentage}%`}
					bg={color}
					borderLeftRadius="md"
					{...(filledPercentage === 100 && { borderRightRadius: 'md' })}
				/>
				{filledPercentage < 100 && (
					<Box
						w={`${100 - filledPercentage}%`}
						bg={tokens.primitive.light.gray[100]}
						borderRightRadius="md"
					/>
				)}
			</Flex>
			<Heading size="lg" mb={1} color={tokens.primitive.light.gray[900]}>
				{value}
			</Heading>
			<Text fontSize="sm" color={tokens.primitive.light.gray[600]}>
				{description}
			</Text>
		</Box>
	)
}

export const ConversionFunnel = ({ meetingsBookedData, isLoading }: Props) => {
	const totalSubmissions =
		meetingsBookedData?.reduce(
			(sum, submission) =>
				sum + parseInt(String(submission.submissions_count || '0'), 10),
			0,
		) || 0

	const tokens = ddsTokens[DDSTheme.light]

	const totalSchedulerViews =
		meetingsBookedData?.reduce(
			(sum, submission) =>
				sum + parseInt(String(submission.scheduler_views || '0'), 10),
			0,
		) || 0

	const totalMeetingsBooked21d =
		meetingsBookedData?.reduce(
			(sum, submission) =>
				sum + parseInt(String(submission.meetings_in_21d || '0'), 10),
			0,
		) || 0

	const schedulerViewPercentage =
		totalSubmissions > 0
			? Math.round((totalSchedulerViews / totalSubmissions) * 100)
			: 0

	const meetingsBookedOfSchedulerViewsPercentage =
		totalSchedulerViews > 0
			? Math.round((totalMeetingsBooked21d / totalSchedulerViews) * 100)
			: 0

	return (
		<Box
			p={6}
			pb={0}
			borderRadius="md"
			position="relative"
			bg={tokens.primitive.light.white[100]}
		>
			{isLoading && <ChartSpinner />}
			<Heading
				as="h3"
				size="md"
				mb={1}
				color={tokens.primitive.light.gray[900]}
			>
				Form Conversion Funnel
			</Heading>
			<Text fontSize="sm" color={tokens.primitive.light.gray[600]} mb={6}>
				Conversion from form submissions to meetings booked
			</Text>

			<Grid
				templateColumns="repeat(3, 1fr)"
				border="1px solid"
				borderColor={tokens.primitive.light.gray[100]}
				borderRadius="md"
			>
				{/* Form Submissions */}
				<FunnelBox
					title="Form submissions"
					value={totalSubmissions}
					description="Total form submissions"
					filledPercentage={100}
					color={tokens.primitive.light.primary[400]}
					position="first"
				/>

				{/* Scheduler Views */}
				<FunnelBox
					title="Scheduler views"
					value={totalSchedulerViews}
					description={`${schedulerViewPercentage}% of form submissions`}
					filledPercentage={schedulerViewPercentage}
					color={tokens.primitive.light.warning[400]}
					position="middle"
				/>

				{/* Meetings Booked */}
				<FunnelBox
					title="Meetings booked"
					value={totalMeetingsBooked21d}
					description={`${meetingsBookedOfSchedulerViewsPercentage}% of scheduler views`}
					filledPercentage={meetingsBookedOfSchedulerViewsPercentage}
					color={tokens.primitive.light.success[400]}
					position="last"
				/>
			</Grid>
		</Box>
	)
}
